import React from 'react';
import { Plus, Trash2, Users, User, Star } from 'lucide-react';
import FormInput from '../../FormInput';

const JuryForm = ({
  formData,
  formErrors,
  selectedJury,
  onInputChange,
  onSubmit,
  submitting,
  typesSoutenance,
  enseignantsInternes,
  enseignantsExternes
}) => {
  const roles = [
    'RAPPORTEUR',
    'EXAMINATEUR', 
    'DIRECTEUR',
    'CODIRECTEUR',
    'INVITE'
  ];

  const addMembre = () => {
    const membres = [...(formData.membres || [])];
    membres.push({
      enseignant_interne: '',
      enseignant_externe: '',
      role: 'EXAMINATEUR'
    });
    onInputChange({ target: { name: 'membres', value: membres } });
  };

  const removeMembre = (index) => {
    const membres = [...(formData.membres || [])];
    membres.splice(index, 1);
    onInputChange({ target: { name: 'membres', value: membres } });
  };

  const updateMembre = (index, field, value) => {
    const membres = [...(formData.membres || [])];
    membres[index] = { ...membres[index], [field]: value };
    
    // Si on sélectionne un enseignant interne, vider l'externe (et vice versa)
    if (field === 'enseignant_interne' && value) {
      membres[index].enseignant_externe = '';
    } else if (field === 'enseignant_externe' && value) {
      membres[index].enseignant_interne = '';
    }
    
    onInputChange({ target: { name: 'membres', value: membres } });
  };

  return (
    <form id="jury-form" onSubmit={onSubmit} className="space-y-6">
      {/* Informations générales */}
      <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
        <h4 className="text-md font-medium text-purple-900 mb-3 flex items-center space-x-2">
          <Users className="h-5 w-5" />
          <span>Informations générales</span>
        </h4>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormInput
            label="Nom du jury"
            name="nom"
            value={formData.nom || ''}
            onChange={onInputChange}
            error={formErrors.nom}
            required
            placeholder="Ex: Jury Master Informatique 2024"
          />

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Type de soutenance *
            </label>
            <select
              name="type_soutenance"
              value={formData.type_soutenance || ''}
              onChange={onInputChange}
              required
              className={`w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors ${
                formErrors.type_soutenance ? 'border-red-300' : 'border-gray-300'
              }`}
            >
              <option value="">Sélectionner un type</option>
              {typesSoutenance.map(type => (
                <option key={type.id} value={type.id}>
                  {type.nom}
                </option>
              ))}
            </select>
            {formErrors.type_soutenance && (
              <p className="text-red-600 text-xs mt-1">{formErrors.type_soutenance}</p>
            )}
          </div>
        </div>
      </div>

      {/* Président */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
        <h4 className="text-md font-medium text-amber-900 mb-3 flex items-center space-x-2">
          <Star className="h-5 w-5" />
          <span>Président du jury</span>
        </h4>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Président interne
            </label>
            <select
              name="president_interne"
              value={formData.president_interne || ''}
              onChange={(e) => {
                onInputChange(e);
                if (e.target.value) {
                  onInputChange({ target: { name: 'president_externe', value: '' } });
                }
              }}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors"
            >
              <option value="">Sélectionner un enseignant interne</option>
              {enseignantsInternes.map(enseignant => (
                <option key={enseignant.id} value={enseignant.id}>
                  {enseignant.utilisateur?.first_name} {enseignant.utilisateur?.last_name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Président externe
            </label>
            <select
              name="president_externe"
              value={formData.president_externe || ''}
              onChange={(e) => {
                onInputChange(e);
                if (e.target.value) {
                  onInputChange({ target: { name: 'president_interne', value: '' } });
                }
              }}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors"
            >
              <option value="">Sélectionner un enseignant externe</option>
              {enseignantsExternes.map(enseignant => (
                <option key={enseignant.id} value={enseignant.id}>
                  {enseignant.nom_complet}
                </option>
              ))}
            </select>
          </div>
        </div>
        <p className="text-xs text-gray-600 mt-2">
          * Sélectionnez soit un président interne, soit un président externe
        </p>
      </div>

      {/* Membres */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-md font-medium text-blue-900 flex items-center space-x-2">
            <User className="h-5 w-5" />
            <span>Membres du jury</span>
          </h4>
          <button
            type="button"
            onClick={addMembre}
            className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700 transition-colors flex items-center space-x-1"
          >
            <Plus className="h-4 w-4" />
            <span>Ajouter membre</span>
          </button>
        </div>

        <div className="space-y-4">
          {(formData.membres || []).map((membre, index) => (
            <div key={index} className="bg-white border border-blue-200 p-4 rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-medium text-gray-700">
                  Membre {index + 1}
                </span>
                <button
                  type="button"
                  onClick={() => removeMembre(index)}
                  className="text-red-500 hover:text-red-700 text-sm flex items-center space-x-1"
                >
                  <Trash2 className="h-4 w-4" />
                  <span>Supprimer</span>
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Enseignant interne
                  </label>
                  <select
                    value={membre.enseignant_interne || ''}
                    onChange={(e) => updateMembre(index, 'enseignant_interne', e.target.value)}
                    className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:ring-1 focus:ring-purple-500"
                  >
                    <option value="">Sélectionner</option>
                    {enseignantsInternes.map(enseignant => (
                      <option key={enseignant.id} value={enseignant.id}>
                        {enseignant.utilisateur?.first_name} {enseignant.utilisateur?.last_name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Enseignant externe
                  </label>
                  <select
                    value={membre.enseignant_externe || ''}
                    onChange={(e) => updateMembre(index, 'enseignant_externe', e.target.value)}
                    className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:ring-1 focus:ring-purple-500"
                  >
                    <option value="">Sélectionner</option>
                    {enseignantsExternes.map(enseignant => (
                      <option key={enseignant.id} value={enseignant.id}>
                        {enseignant.nom_complet}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Rôle
                  </label>
                  <select
                    value={membre.role || 'EXAMINATEUR'}
                    onChange={(e) => updateMembre(index, 'role', e.target.value)}
                    className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:ring-1 focus:ring-purple-500"
                  >
                    {roles.map(role => (
                      <option key={role} value={role}>
                        {role}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          ))}
        </div>

        {(!formData.membres || formData.membres.length === 0) && (
          <p className="text-sm text-gray-500 text-center py-4">
            Aucun membre ajouté. Cliquez sur "Ajouter membre" pour commencer.
          </p>
        )}
      </div>
    </form>
  );
};

export default JuryForm; 